import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { formatDate, parseToISO } from "../utils/dateUtils";
import "./AdministradorPagosModal.css";

const API_BASE = `${process.env.REACT_APP_API_URL || ""}`;

const MEDIOS_PAGO = [
  { value: "EFECTIVO", label: "Efectivo" },
  { value: "TRANSFERENCIA", label: "Transferencia" },
  { value: "TARJETA_DEBITO", label: "Tarjeta Débito" },
  { value: "TARJETA_CREDITO", label: "Tarjeta Crédito" },
];

export default function AdministradorPagosModal({
  isOpen,
  onClose,
  contratoId,
  onUpdated,
}) {
  const [loading, setLoading] = useState(true);
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showCronograma, setShowCronograma] = useState(false);

  const [editingPago, setEditingPago] = useState(null);

  const [form, setForm] = useState({
    fecha_pago: new Date().toISOString().split("T")[0],
    medio_pago: "TRANSFERENCIA",
    valor: "",
    observacion: "",
  });

  const token = localStorage.getItem("token");
  const authConfig = {
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    },
  };

  const cargarEstadoCuenta = useCallback(async () => {
    if (!contratoId) return;
    try {
      setLoading(true);
      setErrorMsg("");
      const res = await axios.get(
        `${API_BASE}/api/contratos/${contratoId}/estado-cuenta/`,
        authConfig
      );
      setEstadoCuenta(res.data);
      if (!editingPago) {
        if (res.data && res.data.saldo > 0) {
          setForm((prev) => ({
            ...prev,
            valor: res.data.saldo.toString(),
          }));
        } else if (res.data) {
          setForm((prev) => ({
            ...prev,
            valor: res.data.canon_actual.toString(),
          }));
        }
      }
    } catch (err) {
      console.error("Error al cargar estado de cuenta:", err);
      setErrorMsg("No fue posible cargar el estado de cuenta del contrato.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoId, token, editingPago]);

  useEffect(() => {
    if (isOpen && contratoId) {
      cargarEstadoCuenta();
    }
  }, [isOpen, contratoId, cargarEstadoCuenta]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleIniciarEdicion = (pago) => {
    setEditingPago(pago);
    setForm({
      fecha_pago: pago.fecha_pago,
      medio_pago: pago.medio_pago,
      valor: pago.valor.toString(),
      observacion: pago.observacion || "",
    });
  };

  const handleCancelarEdicion = () => {
    setEditingPago(null);
    setForm({
      fecha_pago: new Date().toISOString().split("T")[0],
      medio_pago: "TRANSFERENCIA",
      valor: estadoCuenta?.saldo > 0 ? estadoCuenta.saldo.toString() : (estadoCuenta?.canon_actual?.toString() || ""),
      observacion: "",
    });
  };

  const handleRegistrarPago = async (e) => {
    e.preventDefault();
    if (!form.valor || parseFloat(form.valor) <= 0) {
      alert("Por favor ingrese un valor de pago válido mayor a cero.");
      return;
    }

    // Regla de negocio: Contratos finalizados solo pueden recibir pagos con fecha anterior a la fecha de finalización
    const fechaPagoISO = parseToISO(form.fecha_pago);
    if (estadoCuenta && !estadoCuenta.vigente && estadoCuenta.fecha_entrega) {
      if (fechaPagoISO >= estadoCuenta.fecha_entrega) {
        alert(
          `Los contratos finalizados solo pueden recibir a través del Administrador de Pagos de Canon pagos con fecha anterior a la fecha de finalización (${formatDate(estadoCuenta.fecha_entrega)}).`
        );
        return;
      }
    }

    try {
      setSubmitting(true);
      if (editingPago) {
        await axios.patch(
          `${API_BASE}/api/pagos-canon/${editingPago.id}/`,
          {
            fecha_pago: fechaPagoISO,
            medio_pago: form.medio_pago,
            valor: parseFloat(form.valor),
            observacion: form.observacion,
          },
          authConfig
        );
        setEditingPago(null);
      } else {
        await axios.post(
          `${API_BASE}/api/pagos-canon/`,
          {
            contrato: contratoId,
            fecha_pago: fechaPagoISO,
            medio_pago: form.medio_pago,
            valor: parseFloat(form.valor),
            observacion: form.observacion,
          },
          authConfig
        );
      }

      setForm({
        fecha_pago: new Date().toISOString().split("T")[0],
        medio_pago: "TRANSFERENCIA",
        valor: "",
        observacion: "",
      });

      await cargarEstadoCuenta();
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Error al registrar/actualizar pago:", err);
      alert(
        err.response?.data?.detail ||
          "No fue posible guardar el registro de pago. Verifique los datos."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEliminarPago = async (pagoId) => {
    if (!window.confirm("¿Confirma que desea anular o eliminar este registro de pago?")) {
      return;
    }

    try {
      await axios.delete(`${API_BASE}/api/pagos-canon/${pagoId}/`, authConfig);
      await cargarEstadoCuenta();
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Error al eliminar pago:", err);
      alert("No fue posible eliminar el registro de pago.");
    }
  };

  const formatCOP = (val) => {
    if (val === undefined || val === null) return "$0";
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(val);
  };

  if (!isOpen) return null;

  return (
    <div className="pago-modal-backdrop" onClick={onClose}>
      <div
        className="pago-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Encabezado */}
        <div className="pago-modal-header">
          <div>
            <div className="pago-modal-eyebrow">
              Administrador de Pagos de Canon de Arriendo
            </div>
            <h2 className="pago-modal-title">
              Contrato #{contratoId}
              {estadoCuenta && (
                <span
                  className={`pago-badge-vigente ${
                    estadoCuenta.vigente ? "pago-badge--vigente" : "pago-badge--finalizado"
                  }`}
                >
                  {estadoCuenta.vigente ? "Contrato Vigente" : "Contrato Finalizado"}
                </span>
              )}
            </h2>
            {estadoCuenta && (
              <div className="pago-modal-sub">
                <strong>Inmueble:</strong> {estadoCuenta.direccion} |{" "}
                <strong>Matrícula:</strong> {estadoCuenta.matricula} |{" "}
                <strong>Inicio:</strong> {formatDate(estadoCuenta.fecha_inicio)}
                {estadoCuenta.fecha_entrega && (
                  <> | <strong style={{ color: "#b91c1c" }}>Entrega:</strong> {formatDate(estadoCuenta.fecha_entrega)}</>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className="pago-modal-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            &times;
          </button>
        </div>

        {/* Cuerpo */}
        <div className="pago-modal-body">
          {loading ? (
            <div className="pago-loading">Cargando estado de cuenta del contrato...</div>
          ) : errorMsg ? (
            <div className="pago-error">{errorMsg}</div>
          ) : estadoCuenta ? (
            <>
              {/* Banner de Saldo */}
              <div
                className={`pago-balance-banner ${
                  estadoCuenta.esta_al_dia
                    ? "pago-balance-banner--aldia"
                    : "pago-balance-banner--pendiente"
                }`}
              >
                <div className="pago-balance-icon">
                  {estadoCuenta.esta_al_dia ? "✅" : "⚠️"}
                </div>
                <div className="pago-balance-info">
                  <span className="pago-balance-label">Estado de la Cuenta:</span>
                  <div className="pago-balance-val">
                    {estadoCuenta.esta_al_dia
                      ? "AL DÍA (Saldo: $0 COP)"
                      : `SALDO PENDIENTE: ${formatCOP(estadoCuenta.saldo)} COP`}
                  </div>
                  <span className="pago-balance-detail">
                    {estadoCuenta.esta_al_dia
                      ? "No tiene meses pendientes de pago. Todos los periodos causados están al día."
                      : `Tiene aproximadamente ${estadoCuenta.meses_pendientes} mes(es) pendientes por pagar.`}
                  </span>
                </div>
              </div>

              {/* Métricas clave */}
              <div className="pago-metrics-grid">
                <div className="pago-metric-card">
                  <span className="pago-metric-label">Canon Inicial</span>
                  <strong className="pago-metric-val">
                    {formatCOP(estadoCuenta.canon_inicial)}
                  </strong>
                </div>

                <div className="pago-metric-card">
                  <span className="pago-metric-label">Canon Actual Vigente</span>
                  <strong className="pago-metric-val" style={{ color: "#0284c7" }}>
                    {formatCOP(estadoCuenta.canon_actual)}
                  </strong>
                  <span className="pago-metric-sub">Ajustado por IPC anual</span>
                </div>

                <div className="pago-metric-card">
                  <span className="pago-metric-label">Meses Transcurridos</span>
                  <strong className="pago-metric-val">
                    {estadoCuenta.meses_transcurridos}
                  </strong>
                  <span className="pago-metric-sub">Desde fecha inicial</span>
                </div>

                <div className="pago-metric-card">
                  <span className="pago-metric-label">Total Causado / Exigible</span>
                  <strong className="pago-metric-val">
                    {formatCOP(estadoCuenta.total_exigible)}
                  </strong>
                </div>

                <div className="pago-metric-card">
                  <span className="pago-metric-label">Total Pagado Registrado</span>
                  <strong className="pago-metric-val" style={{ color: "#16a34a" }}>
                    {formatCOP(estadoCuenta.total_pagado)}
                  </strong>
                </div>
              </div>

              {/* Formulario de Registro o Edición de Pago */}
              <div className="pago-card-section">
                {editingPago && (
                  <div className="pago-edit-banner">
                    <span>
                      <strong>Modificando Pago #{editingPago.id}</strong> ({formatDate(editingPago.fecha_pago)} - {formatCOP(editingPago.valor)})
                    </span>
                    <button
                      type="button"
                      className="pago-btn-cancel-edit"
                      onClick={handleCancelarEdicion}
                    >
                      ✕ Cancelar Edición
                    </button>
                  </div>
                )}
                <h3 className="pago-section-title">
                  <span>{editingPago ? "✏️" : "💳"}</span>{" "}
                  {editingPago
                    ? `Modificar Pago Registrado #${editingPago.id}`
                    : "Registrar Nuevo Pago de Canon"}
                </h3>
                <form onSubmit={handleRegistrarPago} className="pago-form-grid">
                  <div className="pago-form-group">
                    <label>Fecha de Pago (dd/mm/aaaa) *</label>
                    <input
                      type="date"
                      name="fecha_pago"
                      value={form.fecha_pago}
                      max={
                        estadoCuenta && !estadoCuenta.vigente && estadoCuenta.fecha_entrega
                          ? (() => {
                              const d = new Date(estadoCuenta.fecha_entrega + "T00:00:00");
                              d.setDate(d.getDate() - 1);
                              return d.toISOString().split("T")[0];
                            })()
                          : undefined
                      }
                      onChange={handleInputChange}
                      required
                      className="pago-input"
                    />
                    {estadoCuenta && !estadoCuenta.vigente && estadoCuenta.fecha_entrega && (
                      <small style={{ color: "#b91c1c", fontSize: "0.75rem", marginTop: "2px" }}>
                        ⚠️ Contrato finalizado el {formatDate(estadoCuenta.fecha_entrega)}. Solo se admiten pagos con fecha anterior a su finalización.
                      </small>
                    )}
                  </div>

                  <div className="pago-form-group">
                    <label>Medio de Pago *</label>
                    <select
                      name="medio_pago"
                      value={form.medio_pago}
                      onChange={handleInputChange}
                      required
                      className="pago-input"
                    >
                      {MEDIOS_PAGO.map((mp) => (
                        <option key={mp.value} value={mp.value}>
                          {mp.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pago-form-group">
                    <label>Valor Pagado (COP) *</label>
                    <input
                      type="number"
                      step="any"
                      name="valor"
                      placeholder="Ej: 1000000"
                      value={form.valor}
                      onChange={handleInputChange}
                      required
                      className="pago-input"
                    />
                  </div>

                  <div className="pago-form-group" style={{ gridColumn: "span 2" }}>
                    <label>Observación / Nota (Opcional)</label>
                    <input
                      type="text"
                      name="observacion"
                      placeholder="Ej: Comprobante transferencia Bancolombia #12345"
                      value={form.observacion}
                      onChange={handleInputChange}
                      className="pago-input"
                    />
                  </div>

                  <div className="pago-form-actions" style={{ gridColumn: "1 / -1", display: "flex", gap: "10px" }}>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="pago-btn-submit"
                    >
                      {submitting
                        ? "Guardando..."
                        : editingPago
                        ? "💾 Guardar Cambios"
                        : "💾 Registrar Pago"}
                    </button>
                    {editingPago && (
                      <button
                        type="button"
                        onClick={handleCancelarEdicion}
                        className="pago-btn-cancel-edit"
                        style={{ padding: "10px 18px", fontSize: "0.9rem" }}
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* Historial de Pagos con Scroll Vertical para 12 Pagos */}
              <div className="pago-card-section">
                <h3 className="pago-section-title">
                  <span>📋</span> Historial de Pagos Registrados (
                  {estadoCuenta.pagos.length})
                </h3>
                {estadoCuenta.pagos.length === 0 ? (
                  <div className="pago-empty-msg">
                    Aún no se han registrado pagos para este contrato.
                  </div>
                ) : (
                  <div className="pago-table-scroll-12">
                    <table className="pago-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Medio de Pago</th>
                          <th>Valor</th>
                          <th>Observación</th>
                          <th>Registrado Por</th>
                          <th style={{ textAlign: "center" }}>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {estadoCuenta.pagos.map((p) => (
                          <tr key={p.id}>
                            <td>
                              <strong>{formatDate(p.fecha_pago)}</strong>
                            </td>
                            <td>
                              <span className="pago-pill-medio">
                                {p.medio_pago_display || p.medio_pago}
                              </span>
                            </td>
                            <td>
                              <span className="pago-val-green">
                                {formatCOP(p.valor)}
                              </span>
                            </td>
                            <td>{p.observacion || "—"}</td>
                            <td>
                              <small style={{ color: "#64748b" }}>
                                {p.username || "Sistema"}
                              </small>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <div className="pago-actions-cell" style={{ justifyContent: "center" }}>
                                <button
                                  type="button"
                                  className="pago-btn-edit"
                                  onClick={() => handleIniciarEdicion(p)}
                                  title="Modificar este pago"
                                >
                                  ✏️ Editar
                                </button>
                                <button
                                  type="button"
                                  className="pago-btn-delete"
                                  onClick={() => handleEliminarPago(p.id)}
                                  title="Eliminar registro"
                                >
                                  🗑️ Anular
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Botón para ver u ocultar el desglose de meses e IPC */}
              <div className="pago-card-section">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => setShowCronograma((prev) => !prev)}
                >
                  <h3 className="pago-section-title" style={{ margin: 0 }}>
                    <span>📈</span> Desglose Mensual e Incremento Anual del IPC (
                    {estadoCuenta.cronograma.length} meses)
                  </h3>
                  <button type="button" className="pago-btn-toggle">
                    {showCronograma ? "▲ Ocultar Desglose" : "▼ Ver Desglose por Mes"}
                  </button>
                </div>

                {showCronograma && (
                  <div style={{ marginTop: "16px" }}>
                    <p style={{ fontSize: "0.85rem", color: "#64748b", margin: "0 0 12px 0" }}>
                      El canon se ajusta anualmente con el IPC configurado en Parametrización. Se muestran los pagos aplicados en cada período y el saldo acumulado mes a mes.
                    </p>
                    <div className="pago-table-wrap">
                      <table className="pago-table">
                        <thead>
                          <tr>
                            <th>Mes #</th>
                            <th>Periodo</th>
                            <th>Año Contrato</th>
                            <th>Incremento IPC</th>
                            <th>Canon Exigible</th>
                            <th>Pagos en el Mes</th>
                            <th>Saldo Mes a Mes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {estadoCuenta.cronograma.map((m) => (
                            <tr
                              key={m.mes_numero}
                              style={{
                                backgroundColor:
                                  m.ano_contrato > 1 ? "#f8fafc" : "transparent",
                              }}
                            >
                              <td>
                                <strong>Mes {m.mes_numero}</strong>
                              </td>
                              <td>
                                {formatDate(m.fecha_desde)} al {formatDate(m.fecha_hasta)}
                              </td>
                              <td>Año {m.ano_contrato}</td>
                              <td>
                                {m.ipc_aplicado_pct > 0 ? (
                                  <span
                                    style={{
                                      backgroundColor: "#e0f2fe",
                                      color: "#0369a1",
                                      padding: "2px 8px",
                                      borderRadius: "12px",
                                      fontSize: "0.8rem",
                                      fontWeight: "600",
                                    }}
                                  >
                                    +{m.ipc_aplicado_pct}% ({m.ano_calendario})
                                  </span>
                                ) : (
                                  <span style={{ color: "#94a3b8" }}>Base</span>
                                )}
                              </td>
                              <td>
                                <strong>{formatCOP(m.canon)}</strong>
                              </td>
                              <td>
                                {m.total_pagado_mes > 0 ? (
                                  <div>
                                    <span className="pago-val-green">
                                      {formatCOP(m.total_pagado_mes)}
                                    </span>
                                    {m.pagos_mes && m.pagos_mes.length > 0 && (
                                      <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "2px" }}>
                                        {m.pagos_mes.map((pm, pidx) => (
                                          <span key={pidx} style={{ display: "block" }}>
                                            • {formatDate(pm.fecha_pago)}: {formatCOP(pm.valor)}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span style={{ color: "#94a3b8" }}>$0</span>
                                )}
                              </td>
                              <td>
                                {m.esta_al_dia_mes || m.saldo_mes === 0 ? (
                                  <span className="pago-pill-aldia">✓ Al día ($0)</span>
                                ) : (
                                  <span className="pago-pill-pendiente">
                                    ⚠️ {formatCOP(m.saldo_mes)}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>

        {/* Pie de modal */}
        <div className="pago-modal-footer">
          <button type="button" className="pago-btn-close" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
